import {
  CandidateFinancialExpectationsDtoEmploymentFormEnum,
  CandidateFinancialExpectationsDtoTaxationEnum,
  CvSearchProcessStatusDto,
  CvSearchStepNameDto,
  CvSearchStepStatusDto,
} from '@/api/generated';

export type CvSearchTranslation = {
  columns: {
    actions: string;
    createdAt: string;
    name: string;
    status: string;
  };
  create: {
    descriptionTip: string;
    draftStatus: string;
    jobDescriptionLabel: string;
    jobDescriptionPlaceholder: string;
    nameLabel: string;
    namePlaceholder: string;
    processName: string;
    start: string;
    title: string;
  };
  defaultCvSearch: {
    badge: string;
    candidates: string;
    candidates_few?: string;
    candidates_many?: string;
    candidates_one: string;
    candidates_other: string;
    create: string;
    error: string;
    errorTooltip: string;
    filtering: string;
    generating: string;
    open: string;
    setDefault: string;
    tooltip: string;
  };
  details: {
    backToCvSearch: string;
    backToProcess: string;
    debugData: string;
    empty: string;
    error: {
      description: string;
      hint: string;
      title: string;
    };
    fields: {
      createdAt: string;
      externalProcessId: string;
      id: string;
      lastStatusSyncAt: string;
      processStatus: string;
    };
    inferredTags: {
      addMenu: {
        backToAll: string;
        noCategories: string;
        noResults: string;
        searchPlaceholder: string;
      };
      buttons: {
        add: string;
        remove: string;
      };
      description: string;
      dictionaryError: string;
      empty: string;
      error: string;
      fields: {
        category: string;
        value: string;
      };
      removeConfirmation: string;
      suggestionsTitle: string;
      title: string;
      uncategorized: string;
    };
    keywords: {
      addPlaceholder: string;
      buttons: {
        add: string;
        cancel: string;
        save: string;
      };
      categories: {
        mustHave: string;
        niceToHave: string;
      };
      categoryEmpty: string;
      count: string;
      deleteTooltip: string;
      description: string;
      dragCancelled: string;
      dragDropped: string;
      dragInstructions: string;
      dragOver: string;
      dragStarted: string;
      dragTooltip: string;
      duplicateError: string;
      editTooltip: string;
      empty: string;
      error: string;
      readOnlyDescription: string;
      saveError: string;
      title: string;
      workbenchDescription: string;
      workbenchTitle: string;
    };
    legacy: {
      description: string;
      title: string;
    };
    pipeline: {
      columns: {
        errorMessage: string;
        finishedAt: string;
        name: string;
        order: string;
        startedAt: string;
        status: string;
      };
      description: string;
      empty: string;
      title: string;
    };
    processRequirements: {
      addAllTags: string;
      appliedLabel: string;
      appliedTitle: string;
      appliedTooltip: string;
      clearAll: string;
      clickToAddTag: string;
      emptyTags: string;
      flowTitle: string;
      summary: string;
      tagAlreadyAdded: string;
      uncategorized: string;
    };
    queryError: string;
    readOnlyCriteria: {
      close: string;
      locationCriteria: {
        includeUnrecognized: string;
        label: string;
        radius: string;
      };
      notReady: string;
      summary: string;
      tagsTitle: string;
      title: string;
    };
    refreshing: string;
    rename: {
      cancel: string;
      edit: string;
      label: string;
    };
    results: {
      addCandidate: string;
      addCandidateError: string;
      addContactError: string;
      aiSummary: string;
      allCriteria: string;
      applicationUnavailable: string;
      badRequest: string;
      candidateAlreadyAssigned: string;
      candidateNotAssigned: string;
      columns: {
        actions: string;
        candidateName: string;
        candidateTags: string;
        contactAddedAt: string;
        contactStatus: string;
        currentPosition: string;
        locationExperience: string;
        matches: string;
        rank: string;
        relevanceScore: string;
        status: string;
      };
      contactList: {
        add: string;
        assign: string;
        assignDeclinedConfirmation: string;
        decline: string;
        markWilling: string;
        notLinkedToProcess: string;
        onList: string;
        remove: string;
        removeConfirmation: string;
        restore: string;
        status: {
          assignedToProcess: string;
          doesNotWantToParticipate: string;
          toContact: string;
          wantsToParticipate: string;
        };
        viewProcess: string;
      };
      contactListError: string;
      contactStatusError: string;
      count: string;
      criteriaEmpty: string;
      empty: string;
      error: string;
      excellentMatch: string;
      experience: {
        months: string;
        months_few?: string;
        months_many?: string;
        months_one: string;
        months_other: string;
        years: string;
        years_few?: string;
        years_many?: string;
        years_one: string;
        years_other: string;
      };
      filters: {
        location: string;
        minExperienceError: string;
        minScore: string;
        minScoreError: string;
        minScorePlaceholder: string;
        searchPhrase: string;
        searchPhrasePlaceholder: string;
      };
      financialExpectations: {
        employmentForms: Record<CandidateFinancialExpectationsDtoEmploymentFormEnum, string>;
        fromApplication: string;
        missing: string;
        more: string;
        process: string;
        taxations: Record<CandidateFinancialExpectationsDtoTaxationEnum, string>;
      };
      goodMatch: string;
      loading: string;
      lowMatch: string;
      matchSummary: string;
      missingValue: string;
      moreTags: string;
      noExpressionMatches: string;
      notInCurrentResults: string;
      profile: {
        basicInfo: string;
        city: string;
        close: string;
        copied: string;
        copy: string;
        cv: string;
        cvError: string;
        cvLoading: string;
        cvMissing: string;
        email: string;
        employment: string;
        employmentCurrent: string;
        employmentMissing: string;
        experience: string;
        languages: string;
        match: string;
        matchGroupCount: string;
        matchMissingLabel: string;
        matchNiceToHaveGroup: string;
        matchOtherGroup: string;
        matchRequiredGroup: string;
        matchRequirementMatched: string;
        matchRequirementMissed: string;
        matchRequirementsCount: string;
        matchRequirementsEmpty: string;
        matchRequirementsGroup: string;
        matchRequirementsLabel: string;
        matchSummary: string;
        missing: string;
        next: string;
        notAnalysed: string;
        panelLabel: string;
        phone: string;
        previous: string;
        profile: string;
        showLess: string;
        showMore: string;
        skills: string;
        source: {
          application: string;
          calculated: string;
          cv: string;
          form: string;
          globalProfile: string;
        };
        summary: string;
        tabsLabel: string;
        tags: string;
        tagsCount: string;
        uncategorized: string;
        voivodeship: string;
      };
      requirements: {
        met: string;
        missed: string;
      };
      showLess: string;
      showMore: string;
      step: string;
      steps: {
        candidates: string;
        descriptions: Record<CvSearchStepNameDto, string>;
        title: string;
      };
      tagMatchedPerTagLabel: string;
      tagMatchedTotal: string;
      tagMatchNegatedLabel: string;
      toolbar: {
        industry: string;
        minMatch: string;
        moreFilters: string;
        process: string;
        processAll: string;
        roleCategory: string;
        search: string;
      };
      views: {
        contactList: string;
        label: string;
        results: string;
      };
    };
    rewindSearch: {
      button: string;
      confirmationContent: string;
    };
    running: {
      description: string;
      hint: string;
      title: string;
    };
    statuses: Record<CvSearchProcessStatusDto, string>;
    stepNames: Record<CvSearchStepNameDto, string>;
    stepStatuses: Record<CvSearchStepStatusDto, string>;
    summaryDescription: string;
    summaryTitle: string;
    tagExpression: {
      addGroup: string;
      addTag: string;
      all: string;
      allConditions: string;
      any: string;
      anyConditions: string;
      collapse: string;
      confirmRemoveGroup: string;
      depth: string;
      dragCancelled: string;
      dragDropped: string;
      dragHandle: string;
      dragInstructions: string;
      dragOver: string;
      dragStarted: string;
      dropIntoGroup: string;
      emptyDescription: string;
      emptyTitle: string;
      expand: string;
      fields: {
        companies: string;
        languages: string;
        tags: string;
      };
      guide: {
        close: string;
        intro: string;
        open: string;
        sections: {
          overview: string;
          semantic: string;
          text: string;
          visual: string;
        };
        steps: {
          addGroup: {
            description: string;
            title: string;
          };
          addTags: {
            description: string;
            title: string;
          };
          anyGroup: {
            description: string;
            title: string;
          };
          negate: {
            description: string;
            title: string;
          };
          notResult: {
            description: string;
            title: string;
          };
          replaceWithParent: {
            description: string;
            title: string;
          };
          rootTags: {
            description: string;
            title: string;
          };
          semantic: {
            description: string;
            title: string;
          };
          stages: {
            description: string;
            title: string;
          };
          start: {
            description: string;
            title: string;
          };
          tagGroups: {
            description: string;
            title: string;
          };
          textMode: {
            description: string;
            title: string;
          };
          textSyntax: {
            description: string;
            title: string;
          };
          tips: {
            description: string;
            title: string;
          };
        };
        title: string;
        video: string;
      };
      howItWorks: {
        all: string;
        allDescription: string;
        any: string;
        anyDescription: string;
        not: string;
        notDescription: string;
        title: string;
      };
      incompleteNot: string;
      invalidTag: string;
      leaves: string;
      matchedCandidates: string;
      menu: {
        addNestedGroup: string;
        deleteGroup: string;
        deleteNode: string;
        unwrapNot: string;
        wrapWithNot: string;
      };
      negate: string;
      notConditions: string;
      notWithoutChild: string;
      operator: string;
      picker: {
        add: string;
        allCategories: string;
        cancel: string;
        categories: string;
        categoryError: string;
        clearSearch: string;
        clearSelected: string;
        groupBadge: string;
        groupSelected: string;
        languageLevelLabel: string;
        languageLevelSelect: string;
        levelAny: string;
        loading: string;
        loadingMore: string;
        loadMore: string;
        noCategories: string;
        noTagsSelected: string;
        noValues: string;
        refresh: string;
        results: string;
        resultsCount_few?: string;
        resultsCount_many?: string;
        resultsCount_one: string;
        resultsCount_other: string;
        retry: string;
        search: string;
        searchPlaceholder: string;
        selectCategory: string;
        selected: string;
        selectedTags: string;
        showMore: string;
        title: string;
        valueError: string;
        values: string;
      };
      preview: string;
      refreshDictionary: string;
      remove: string;
      replace: string;
      required: string;
      strictDescription: string;
      tagDictionary: string;
      tagGroup: {
        alsoMatches: string;
        childLabel: string;
        more: string;
        parentLabel: string;
        partOf: string;
        replaceWithParent: string;
      };
      textMode: {
        errors: {
          'missing-operand': string;
          'missing-operator': string;
          'mixed-operators': string;
          'too-deep': string;
          'too-many-leaves': string;
          'unbalanced-parentheses': string;
          'unconfirmed-tag': string;
        };
        groupBadge: string;
        inputLabel: string;
        limitReached: string;
        modeLabel: string;
        modeText: string;
        modeVisual: string;
        operators: {
          and: string;
          not: string;
          or: string;
        };
        placeholder: string;
        switchBlocked: string;
        toggleOperator: string;
      };
      title: string;
      tooDeep: string;
      tooManyLeaves: string;
      unknownTag: string;
      validationEmptyGroup: string;
      workbenchTitle: string;
    };
    unsavedChanges: string;
  };
  error: string;
  filters: {
    createdBy: string;
    dateFrom: string;
    dateTo: string;
    name: string;
    statuses: string;
  };
  liveEditor: {
    back: string;
    jobDescriptionTip: string;
    keywordsDescription: string;
    locationTitle: string;
    minExperienceError: string;
    minExperienceHint: string;
    minExperiencePlaceholder: string;
    minExperienceTitle: string;
    next: string;
    preview: {
      empty: string;
      error: string;
      loading: string;
      clickHint: string;
      matchedOptional: string;
      noCriteria: string;
      title: string;
      total_few?: string;
      total_many?: string;
      total_one: string;
      total_other: string;
    };
    save: string;
    search: string;
    tagsTitle: string;
  };
  openDetails: string;
  presets: {
    actions: {
      browse: string;
      browseDisabledTooltip: string;
      save: string;
      saveDisabledTooltip: string;
    };
    browser: {
      actionError: string;
      authorAll: string;
      authorLabel: string;
      close: string;
      delete: string;
      deleteConfirm: string;
      empty: string;
      error: string;
      helpLabel: string;
      helpLoad: string;
      helpOverwrite: string;
      load: string;
      searchPlaceholder: string;
      summaryMustHave_few?: string;
      summaryMustHave_many?: string;
      summaryMustHave_one: string;
      summaryMustHave_other: string;
      summaryNiceToHave_few?: string;
      summaryNiceToHave_many?: string;
      summaryNiceToHave_one: string;
      summaryNiceToHave_other: string;
      summaryTags_few?: string;
      summaryTags_many?: string;
      summaryTags_one: string;
      summaryTags_other: string;
      title: string;
      unknownAuthor: string;
      update: string;
      updated: string;
      updateDisabledTooltip: string;
    };
    saveDialog: {
      cancel: string;
      conflict: string;
      error: string;
      nameLabel: string;
      namePlaceholder: string;
      overwrite: string;
      save: string;
      title: string;
    };
  };
  startButton: string;
  startDialog: {
    cancel: string;
    confirm: string;
    emptyNameError: string;
    nameLabel: string;
    title: string;
  };
  startDisabledTooltip: string;
};

export const cvSearchTranslation: CvSearchTranslation = {
  columns: {
    actions: 'Actions',
    createdAt: 'Created at',
    name: 'Name',
    status: 'Status',
  },
  create: {
    descriptionTip: 'Tip: The more details you provide, the better results we can deliver.',
    draftStatus: 'Draft',
    jobDescriptionLabel: 'Job description',
    jobDescriptionPlaceholder:
      "Describe the role, responsibilities, required skills, experience, tools, technologies, and anything else that's important.",
    nameLabel: 'Search name',
    namePlaceholder: 'E.g. Senior Python Developer',
    processName: 'CV Search for {{processName}}',
    start: 'Start',
    title: 'New CV Search',
  },
  defaultCvSearch: {
    badge: 'Default',
    candidates: '{{count}} candidates',
    candidates_one: '{{count}} candidate',
    candidates_other: '{{count}} candidates',
    create: 'Create CV Search',
    error: 'Error',
    errorTooltip: 'Could not load the default CV Search.',
    filtering: 'Filtering…',
    generating: 'Generating suggestions…',
    open: 'Open CV Search',
    setDefault: 'Set as default',
    tooltip: 'Default CV Search: {{name}}',
  },
  details: {
    backToCvSearch: 'Back to CV Search',
    backToProcess: 'Back to process',
    debugData: 'Debug data',
    empty: 'CV Search details are not available.',
    error: {
      description: 'CV Search finished with an error.',
      hint: 'Try restarting the CV Search or contact support if the problem persists.',
      title: 'Something went wrong',
    },
    fields: {
      createdAt: 'Created at',
      externalProcessId: 'External process ID',
      id: 'Internal ID',
      lastStatusSyncAt: 'Last status sync',
      processStatus: 'Pipeline status',
    },
    inferredTags: {
      addMenu: {
        backToAll: 'Back to all',
        noCategories: 'No categories available.',
        noResults: 'No keywords match your search.',
        searchPlaceholder: 'Search',
      },
      buttons: {
        add: 'Add keyword',
        remove: 'Remove keyword',
      },
      description:
        'Review the keywords before the search continues. Remove unnecessary values or add another keyword from the available list.',
      dictionaryError:
        'Could not load the CV Search keyword dictionary. Current keywords are still visible, but adding keywords is unavailable.',
      empty: 'No keywords were returned for this CV Search.',
      error: 'Could not load keywords.',
      fields: {
        category: 'Category',
        value: 'Value',
      },
      removeConfirmation:
        'Remove keyword "{{value}}" from category "{{category}}"? This change will be saved immediately.',
      suggestionsTitle: 'CV Search suggestions',
      title: 'Keywords',
      uncategorized: 'Uncategorized',
    },
    keywords: {
      addPlaceholder: 'Add CV Search requirement...',
      buttons: {
        add: 'Add',
        cancel: 'Cancel',
        save: 'Save',
      },
      categories: {
        mustHave: 'Must have',
        niceToHave: 'Nice to have',
      },
      categoryEmpty: 'No CV Search requirements in this category.',
      count: '{{count}} CV Search requirements',
      deleteTooltip: 'Delete',
      description: 'Define the must-have and nice-to-have CV Search requirements for this search.',
      dragCancelled: 'Drag cancelled.',
      dragDropped: 'CV Search requirement moved.',
      dragInstructions:
        'To pick up a CV Search requirement, press space or enter. While dragging, use the arrow keys to move the CV Search requirement. Press space or enter again to drop it, or press escape to cancel.',
      dragOver: 'CV Search requirement is over category "{{category}}".',
      dragStarted: 'Picked up CV Search requirement "{{value}}".',
      dragTooltip: 'Drag to another category',
      duplicateError: 'This CV Search requirement already exists in the category.',
      editTooltip: 'Click to edit',
      empty: 'No CV Search requirements were returned for this CV Search.',
      error: 'Could not load CV Search requirements.',
      readOnlyDescription: 'CV Search requirements used by this search.',
      saveError: 'Could not save CV Search requirement changes.',
      title: 'CV Search Requirements',
      workbenchDescription:
        'Add must-have and nice-to-have CV Search requirements for this search.',
      workbenchTitle: '2. CV Search Requirements',
    },
    legacy: {
      description:
        'This search was created using the previous keyword filtering system. The original keywords are shown read-only and are not converted to a keyword expression.',
      title: 'Legacy search criteria',
    },
    pipeline: {
      columns: {
        errorMessage: 'Error',
        finishedAt: 'Finished at',
        name: 'Step',
        order: 'Order',
        startedAt: 'Started at',
        status: 'Status',
      },
      description: 'Current execution state of every CV Search pipeline step.',
      empty: 'No pipeline steps have been returned for this CV Search.',
      title: 'Pipeline steps',
    },
    processRequirements: {
      addAllTags: 'Add all',
      appliedLabel: 'AI suggestions',
      appliedTitle: 'Keywords from the job requirements',
      appliedTooltip: 'Click a keyword to add it to the search logic',
      clearAll: 'Clear all',
      clickToAddTag: 'Click to add keyword to expression',
      emptyTags: 'No suggested keywords available.',
      flowTitle: 'Process requirements used for AI suggestions',
      summary: '({{requirements}})',
      tagAlreadyAdded: 'Keyword is already added to expression',
      uncategorized: 'Uncategorized',
    },
    queryError: 'Could not load CV Search details.',
    readOnlyCriteria: {
      close: 'Close criteria',
      locationCriteria: {
        includeUnrecognized: 'including candidates with unrecognized location',
        label: 'Location',
        radius: '{{km}} km radius',
      },
      notReady: ' · expression required',
      summary:
        'Search keywords: {{expression}}{{expressionState}} · CV Search requirements: {{requirements}} · Suggested keywords: {{suggestions}}{{features}}',
      tagsTitle: 'Search keywords',
      title: 'Search criteria',
    },
    refreshing: 'Refreshing status...',
    rename: {
      cancel: 'Press Escape to cancel',
      edit: 'Rename search',
      label: 'Search name',
    },
    results: {
      addCandidate: 'To process',
      addCandidateError: 'Could not add candidate to process.',
      addContactError: 'Could not add candidate to the contact list.',
      aiSummary: 'AI summary',
      allCriteria: 'Show all match criteria',
      applicationUnavailable: 'Candidate application data is unavailable.',
      badRequest: 'The match filters are invalid. Check the values and try again.',
      candidateAlreadyAssigned: 'Already assigned',
      candidateNotAssigned: 'Not assigned',
      columns: {
        actions: 'Actions',
        candidateName: 'Name',
        candidateTags: 'Candidate keywords',
        contactAddedAt: 'Added',
        contactStatus: 'List status',
        currentPosition: 'Current position',
        locationExperience: 'Location & experience',
        matches: 'Matches',
        rank: '#',
        relevanceScore: 'Relevance',
        status: 'Status',
      },
      contactList: {
        add: 'To contact',
        assign: 'Assign',
        assignDeclinedConfirmation:
          'This candidate declined to participate. Assign the candidate to the process anyway?',
        decline: 'Decline',
        markWilling: 'Willing',
        notLinkedToProcess: 'This search is not linked to a process.',
        onList: 'On list',
        remove: 'Remove',
        removeConfirmation: 'Remove this candidate from the contact list?',
        restore: 'Restore',
        status: {
          assignedToProcess: 'Assigned',
          doesNotWantToParticipate: 'Declined',
          toContact: 'To contact',
          wantsToParticipate: 'Willing',
        },
        viewProcess: 'View process',
      },
      contactListError: 'Could not load the contact list.',
      contactStatusError: 'Could not update the contact-list decision.',
      count: 'Results: {{count}}',
      criteriaEmpty: 'No criteria returned',
      empty: 'CV Search finished, but no result references were returned.',
      error: 'Could not load CV Search results.',
      excellentMatch: 'Excellent match',
      experience: {
        months: '{{count}} months',
        months_one: '{{count}} month',
        months_other: '{{count}} months',
        years: '{{count}} years',
        years_one: '{{count}} year',
        years_other: '{{count}} years',
      },
      filters: {
        location: 'Location',
        minExperienceError: 'Enter a non-negative whole number of months.',
        minScore: 'Minimum relevance score',
        minScoreError: 'Enter a value from 0 to 100.',
        minScorePlaceholder: '0–100',
        searchPhrase: 'Search candidate',
        searchPhrasePlaceholder: 'Name or surname',
      },
      financialExpectations: {
        employmentForms: { B2B: 'B2B', UOP: 'employment contract' },
        fromApplication: 'Application from {{date}}',
        missing: 'No financial expectations',
        more: '+{{count}}',
        process: 'Recruitment process',
        taxations: { GROSS: 'gross', NET: 'net' },
      },
      goodMatch: 'Good match',
      loading: 'Loading CV Search results...',
      lowMatch: 'Low match',
      matchSummary: 'Match summary',
      missingValue: '—',
      moreTags: '+{{count}}',
      noExpressionMatches: 'No candidates match this keyword expression.',
      notInCurrentResults: 'Not in current results',
      profile: {
        basicInfo: 'Basic information',
        city: 'City',
        close: 'Close profile',
        copied: 'Copied',
        copy: 'Copy',
        cv: 'CV',
        cvError: 'Could not load the candidate CV.',
        cvLoading: 'Loading candidate CV...',
        cvMissing: 'No CV is available for this candidate.',
        email: 'Email',
        employment: 'Employment history',
        employmentCurrent: 'Current',
        employmentMissing: 'No role details',
        experience: 'Total experience',
        languages: 'Languages',
        match: 'Match',
        matchGroupCount: '{{matched}} of {{total}}',
        matchMissingLabel: 'missed',
        matchNiceToHaveGroup: 'Nice to have',
        matchOtherGroup: 'Other requirements',
        matchRequiredGroup: 'Required',
        matchRequirementMatched: 'Met',
        matchRequirementMissed: 'Not met',
        matchRequirementsCount: '{{matched}} of {{total}}',
        matchRequirementsEmpty: 'No match requirements were returned.',
        matchRequirementsGroup: '{{title}} ({{matched}}/{{total}})',
        matchRequirementsLabel: 'met',
        matchSummary: 'Match summary',
        missing: 'No data',
        next: 'Next candidate',
        notAnalysed:
          'This candidate is not in the current results, so they have not been analysed against the current criteria.',
        panelLabel: 'Candidate details',
        phone: 'Phone',
        previous: 'Previous candidate',
        profile: 'Profile',
        showLess: 'Show less',
        showMore: 'Show more',
        skills: 'Technologies and tools',
        source: {
          application: 'Candidate application',
          calculated: 'Calculated value',
          cv: 'CV',
          form: 'Candidate form',
          globalProfile: 'Global profile',
        },
        summary: 'Candidate summary',
        tabsLabel: 'Candidate detail sections',
        tags: 'Candidate keywords',
        tagsCount: 'Keywords · {{count}}',
        uncategorized: 'Uncategorized',
        voivodeship: 'Voivodeship',
      },
      requirements: {
        met: '{{count}} met',
        missed: '{{count}} missed',
      },
      showLess: 'Show less',
      showMore: 'Show more',
      step: 'Result step: {{step}}',
      steps: {
        candidates: '{{input}} → {{output}}',
        descriptions: {
          [CvSearchStepNameDto.DictMap]: 'Mapped candidate data to dictionary values',
          [CvSearchStepNameDto.Enrichment]: 'Enriched candidate profiles',
          [CvSearchStepNameDto.HybridSearch]:
            'Matched candidates by CV Search requirements and profile data',
          [CvSearchStepNameDto.JdAnalysis]: 'Analyzed the job description',
          [CvSearchStepNameDto.LlmExtract]: 'Extracted details from CVs with AI',
          [CvSearchStepNameDto.Persist]: 'Saved the final results',
          [CvSearchStepNameDto.Rerank]: 'Scored and ranked candidates by relevance',
          [CvSearchStepNameDto.RrfFusion]: 'Merged candidate lists from all search sources',
          [CvSearchStepNameDto.StructuralFilter]: 'Filtered candidates by the keyword expression',
        },
        title: 'Details search results',
      },
      tagMatchedPerTagLabel: 'per keyword',
      tagMatchedTotal: 'Keyword criteria matched {{count}} candidates',
      tagMatchNegatedLabel: 'NOT {{label}}',
      toolbar: {
        industry: 'Industry',
        minMatch: 'Min. match: {{value}}%',
        moreFilters: 'More filters',
        process: 'Process',
        processAll: 'Process: All',
        roleCategory: 'Role category',
        search: 'Search in results...',
      },
      views: {
        contactList: 'Contact list ({{count}})',
        label: 'CV Search result views',
        results: 'Results ({{count}})',
      },
    },
    rewindSearch: {
      button: 'Edit search',
      confirmationContent:
        'This action will return the search to query editing. Current results will be lost.',
    },
    running: {
      description:
        'We are matching CVs against your search criteria. This usually takes a moment — the results will appear here automatically.',
      hint: 'You can keep working in another tab; this page refreshes the status on its own.',
      title: 'Analysis in progress',
    },
    statuses: {
      [CvSearchProcessStatusDto.Created]: 'Created',
      [CvSearchProcessStatusDto.Error]: 'Error',
      [CvSearchProcessStatusDto.Finished]: 'Finished',
      [CvSearchProcessStatusDto.Paused]: 'Paused',
      [CvSearchProcessStatusDto.Running]: 'Running',
    },
    stepNames: {
      [CvSearchStepNameDto.DictMap]: 'Dictionary mapping',
      [CvSearchStepNameDto.Enrichment]: 'Enrichment',
      [CvSearchStepNameDto.HybridSearch]: 'Hybrid search',
      [CvSearchStepNameDto.JdAnalysis]: 'Job description analysis',
      [CvSearchStepNameDto.LlmExtract]: 'LLM extraction',
      [CvSearchStepNameDto.Persist]: 'Persist',
      [CvSearchStepNameDto.Rerank]: 'Rerank',
      [CvSearchStepNameDto.RrfFusion]: 'RRF fusion',
      [CvSearchStepNameDto.StructuralFilter]: 'Structural filter',
    },
    stepStatuses: {
      [CvSearchStepStatusDto.Created]: 'Created',
      [CvSearchStepStatusDto.Error]: 'Error',
      [CvSearchStepStatusDto.Finished]: 'Finished',
      [CvSearchStepStatusDto.InProgress]: 'In progress',
    },
    summaryDescription:
      'Status: {{status}}. Created: {{createdAt}}. Internal ID: {{id}}. External process ID: {{externalProcessId}}. Suggestions: {{suggestions}}. Own expression: {{expressionLeaves}} keywords{{expressionState}}.',
    summaryTitle: 'CV Search summary',
    tagExpression: {
      addGroup: 'Add group',
      addTag: 'Add keyword',
      all: 'AND',
      allConditions: 'All of the following conditions must be met',
      any: 'OR',
      anyConditions: 'At least one of the following conditions',
      collapse: 'Collapse group',
      confirmRemoveGroup: 'Remove this group and all keywords inside it?',
      depth: 'Depth: {{count}} / {{max}}',
      dragCancelled: 'Move cancelled.',
      dragDropped: 'Block moved.',
      dragHandle: 'Move block',
      dragInstructions:
        'Press Space or Enter to pick up a block. Use the arrow keys to choose a target, then press Space or Enter to drop it. Press Escape to cancel.',
      dragOver: 'Block is over a drop target.',
      dragStarted: 'Block picked up.',
      dropIntoGroup: 'Drop here to move into this group',
      emptyDescription: 'Start with a keyword or create a group for a nested expression.',
      emptyTitle: 'No keyword expression yet',
      expand: 'Expand group',
      fields: {
        companies: 'Companies',
        languages: 'Languages',
        tags: 'Keywords',
      },
      guide: {
        close: 'Close',
        intro:
          'CV Search finds candidates in two stages. Stage 1 - keywords: an expression of keywords filters the candidate base, and only candidates who match it go further. The list of matching candidates updates as you edit. Stage 2 - semantic search: CV Search requirements and an optional job description rank the filtered candidates by relevance.\nYou can write the keyword expression as text or build it in the visual editor - both modes edit the same logic. Languages and companies have their own fields below the keywords, and all fields are joined with AND, so English goes into the Languages field. The examples build: English AND Sales Consultant AND (Retail OR Telecommunications) AND NOT Copywriter.',
        open: 'Guide',
        sections: {
          overview: 'How the search works',
          semantic: 'Stage 2: semantic search',
          text: 'Stage 1: keywords in the Text mode',
          visual: 'Stage 1: keywords in the Visual mode',
        },
        steps: {
          addGroup: {
            description:
              'Click Add group below the expression to create a nested group, then use Add keyword inside it.\nOpen the operator dropdown in the group header and pick OR when any one of the keywords is enough.',
            title: 'Add a group and change AND to OR',
          },
          addTags: {
            description:
              'Click Add keyword. In the keyword picker type a value or alias, or narrow the list with a category on the left, and tick the keywords you need - you can pick several at once.\nFor roles and languages you can set the minimum level on the right (for example Senior, Proficient). Keywords marked with an arrow belong to a parent keyword group.\nClick Add N to put the selected keywords into the group.',
            title: 'Add keywords',
          },
          anyGroup: {
            description:
              'The nested group now means: Retail OR Telecommunications. Combined with the main group the candidate needs English AND Sales Consultant AND experience in at least one of these industries.\nThe text next to the operator always describes what the group requires.',
            title: 'Read the expression',
          },
          negate: {
            description:
              'To exclude a keyword, put it in its own group (Add group, then Add keyword). Then click the three dots in the group header and choose Negate with NOT.\nThe same menu adds a nested group inside this group or deletes the group with all its keywords.',
            title: 'Exclude candidates with NOT',
          },
          notResult: {
            description:
              'A negated group has an orange frame and the NOT operator: candidates who match it are excluded.\nTip: put a single keyword or an OR group under NOT - then candidates with any of these keywords are excluded. NOT over an AND group with several keywords only excludes candidates who have all of them.\nTo undo the negation, choose Remove NOT negation from the three-dots menu - the group and its keywords stay.',
            title: 'Check the negated group',
          },
          replaceWithParent: {
            description:
              'A keyword that belongs to a group has an arrow icon on its chip. Hover the icon to see the group name and click Replace with group to swap the keyword for its parent - the search then also matches the other keywords of that group. The role or language level stays the same.\nA parent keyword has a tree icon - hover it to see which keywords it also matches.',
            title: 'Widen a keyword to its group',
          },
          rootTags: {
            description:
              'Keywords are listed by category. With AND a candidate must have English and Sales Consultant.\nRemove a keyword with the x on its chip. Chips with an icon belong to a keyword group - see the next steps.',
            title: 'Check the result',
          },
          stages: {
            description:
              'Keywords are a hard filter: a candidate who does not match the expression does not reach the results, however well the CV fits the CV Search requirements. Use keywords for must-have facts - role, language, industry - and for exclusions.\nThe Matching candidates list refreshes after every change, so you see at once whether the expression is too narrow or too broad.\nWhen the expression is ready, click Continue to move to the semantic search. Continue stays disabled until the expression has at least one keyword and no errors.',
            title: 'Two stages: keywords, then semantic search',
          },
          start: {
            description:
              'Click Visual in the header to see the same expression as a tree of groups. Both modes edit the same logic, so you can switch at any time - the builder remembers the mode you used last.\nThe outer frame is the main group. Its operator applies to everything inside it - by default AND, which means every condition must be met. Use the dropdown to switch it to OR.',
            title: 'Switch to the Visual mode',
          },
          semantic: {
            description:
              'Click Continue. Check the search name (suggested from the first keywords), add must-have and nice-to-have CV Search requirements, and optionally paste the job description.\nThe semantic search compares CVs with your CV Search requirements and job description - by exact words and by meaning - and ranks by relevance only the candidates who matched the keywords. The final order comes from this ranking, not from the number of matched keywords.\nClick Search to start the analysis - it needs at least one CV Search requirement or a job description. Back to keywords returns to stage 1 without losing anything, and Save stores the search without running it.',
            title: 'Add CV Search requirements and start the search',
          },
          tagGroups: {
            description:
              'Some keywords are groups: a parent keyword (for example Accountant) collects related keywords (Accountant Assistant, Accounting Clerk...). A parent keyword in the expression also matches every keyword in its group.\nIn the picker a group is shown in bold with "group · N". Expand it with the arrow to see its keywords. Ticking the parent ticks the whole group - only the parent is added, and it covers all its keywords. Unticking one keyword of a ticked group replaces the parent with the remaining keywords of the group.',
            title: 'Keyword groups',
          },
          textMode: {
            description:
              'The Text mode is the default. Click the field and start typing a keyword name, for example Engl - after 2 letters a list of matching keywords appears with their categories. A keyword that belongs to a keyword group shows the group after a dot.\nMove through the list with the Up and Down arrows and confirm with Enter or Tab - the keyword becomes a chip. Then type AND, OR or NOT followed by a space, or pick the operator from the list, and type the next keyword.\nA keyword picked right after another keyword gets AND automatically. Click an AND or OR chip to switch it. Set the role or language level with the pencil on the chip.',
            title: 'Type keywords and operators',
          },
          textSyntax: {
            description:
              'Use ( and ) to group conditions, for example English AND Sales Consultant AND (Retail OR Telecommunications) AND NOT Copywriter. NOT excludes the keyword or the parentheses that follow it.\nAND and OR cannot be mixed at one level without parentheses - write A AND (B OR C), not A AND B OR C - so the expression always means what it shows.\nMove between chips with the Left and Right arrows. Backspace removes the element before the cursor, and the x on a chip removes the keyword together with its operator. Text that was not picked from the list is underlined in red and is not a keyword.\nWhile the expression has an error, a message below the field explains it, and Continue, Save and the Visual mode stay disabled until you fix it.',
            title: 'Group conditions and fix errors',
          },
          tips: {
            description:
              'Hover the question mark next to the title for a short summary of AND, OR and NOT.\nDrag keywords and groups to move them to another group. Collapse a group with the minus button on the left of its header.\nAn expression can have up to 50 keywords and 5 levels of nesting in both modes.',
            title: 'Useful tips',
          },
        },
        title: 'How to build search logic',
        video: 'Video: building search logic step by step',
      },
      howItWorks: {
        all: 'AND',
        allDescription: 'A candidate must match every condition in the group.',
        any: 'OR',
        anyDescription: 'A candidate must match at least one condition in the group.',
        not: 'NOT',
        notDescription: 'A candidate must not match the condition.',
        title: 'How it works',
      },
      incompleteNot: 'NOT ?',
      invalidTag: 'This keyword is no longer available.',
      leaves: '{{count}} / {{max}} keywords',
      matchedCandidates: '{{count}} matching candidates',
      menu: {
        addNestedGroup: 'Add nested group',
        deleteGroup: 'Delete group',
        deleteNode: 'Delete condition',
        unwrapNot: 'Remove NOT negation',
        wrapWithNot: 'Negate with NOT',
      },
      negate: 'NOT',
      notConditions: 'None of the following conditions can be met',
      notWithoutChild: 'NOT needs one keyword or group.',
      operator: 'Operator',
      picker: {
        add: 'Add {{count}}',
        allCategories: 'All categories',
        cancel: 'Cancel',
        categories: 'Categories',
        categoryError: 'Could not load keyword categories.',
        clearSearch: 'Clear search',
        clearSelected: 'Clear all',
        groupBadge: 'group · {{count}}',
        groupSelected: '{{selected}} of {{count}}',
        languageLevelLabel: 'Language level',
        languageLevelSelect: 'Select minimum language level for {{value}}',
        levelAny: 'Any level',
        loading: 'Loading keywords...',
        loadingMore: 'Loading more...',
        loadMore: 'Load more',
        noCategories: 'No keyword categories are available.',
        noTagsSelected: 'No keywords selected yet',
        noValues: 'No keywords match this search.',
        refresh: 'Refresh dictionary',
        results: '{{count}} results',
        resultsCount_one: '1 result',
        resultsCount_other: '{{count}} results',
        retry: 'Try again',
        search: 'Search keywords',
        searchPlaceholder: 'Search by value or alias',
        selectCategory: 'Select a category to see its keywords.',
        selected: 'Selected: {{count}}',
        selectedTags: 'Selected keywords ({{count}}):',
        showMore: 'Show {{count}} more',
        title: 'Choose keywords',
        valueError: 'Could not load keyword values.',
        values: 'Keyword values',
      },
      preview: 'Preview: {{value}}',
      refreshDictionary: 'Refresh keyword dictionary',
      remove: 'Remove',
      replace: 'Replace',
      required: 'Add at least one keyword before continuing the search.',
      strictDescription: 'Use AND, OR and NOT to build nested keyword criteria.',
      tagDictionary: 'TAG DICTIONARY',
      tagGroup: {
        alsoMatches: 'Also matches keywords in this group ({{count}}):',
        childLabel: 'Show keyword group',
        more: '+{{count}} more',
        parentLabel: 'Show keywords in this group',
        partOf: 'Part of group {{value}}',
        replaceWithParent: 'Replace with group',
      },
      textMode: {
        errors: {
          'missing-operand':
            'A keyword or group is missing next to an operator or inside parentheses.',
          'missing-operator': 'Put AND or OR between two conditions.',
          'mixed-operators':
            'AND and OR are mixed at the same level. Add parentheses to show which goes together.',
          'too-deep': 'The expression can be nested up to {{count}} levels.',
          'too-many-leaves': 'The expression can have up to {{count}} keywords.',
          'unbalanced-parentheses': 'Every opening parenthesis needs a closing one.',
          'unconfirmed-tag': 'Pick a keyword from the list - free text is not a keyword.',
        },
        groupBadge: 'group · {{count}}',
        inputLabel: 'Search logic',
        limitReached: 'The keyword limit ({{count}}) is reached.',
        modeLabel: 'Editing mode',
        modeText: 'Text',
        modeVisual: 'Visual',
        operators: {
          and: 'both conditions',
          not: 'exclude the next condition',
          or: 'either condition',
        },
        placeholder: 'Type a keyword name, e.g. Java, then AND, OR, NOT…',
        switchBlocked: 'Fix the expression to switch to the visual mode.',
        toggleOperator: 'Switch between AND and OR',
      },
      title: 'Keyword expression',
      tooDeep: 'The expression can be nested up to {{count}} levels.',
      tooManyLeaves: 'The expression can contain up to {{count}} keywords.',
      unknownTag: 'Keyword #{{id}}',
      validationEmptyGroup: 'This group needs at least one keyword or nested group.',
      workbenchTitle: '1. Define search logic',
    },
    unsavedChanges: 'You have unsaved search changes. Leave this view anyway?',
  },
  error: 'Could not load CV Search processes.',
  filters: {
    createdBy: 'Created by',
    dateFrom: 'Date from',
    dateTo: 'Date to',
    name: 'Name',
    statuses: 'Statuses',
  },
  liveEditor: {
    back: 'Back to keywords',
    jobDescriptionTip:
      'Optional. A job description lets the analysis extract additional CV Search requirements and rank candidates better.',
    keywordsDescription:
      'Add CV Search requirements the analysis should look for in CVs. Search requires at least one CV Search requirement or a job description.',
    locationTitle: 'Location',
    minExperienceError: 'Enter a non-negative whole number of months.',
    minExperienceHint: 'Total work experience from the CV, in months. Empty = no filter.',
    minExperiencePlaceholder: 'e.g. 36',
    minExperienceTitle: 'Minimum experience (months)',
    next: 'Continue',
    preview: {
      empty: 'No candidates match these keywords.',
      error: 'Could not load the preview. Try changing the criteria.',
      loading: 'Loading candidates…',
      clickHint: 'Click a candidate to see their profile and CV.',
      matchedOptional: '{{matched}} of {{total}}',
      noCriteria: 'Add keywords to see matching candidates.',
      title: 'Matching candidates',
      total_one: '1 candidate',
      total_other: '{{count}} candidates',
    },
    save: 'Save',
    search: 'Search',
    tagsTitle: 'Keywords',
  },
  openDetails: 'Open details',
  presets: {
    actions: {
      browse: 'Saved filters',
      browseDisabledTooltip: 'Filters can be loaded only while a search is paused.',
      save: 'Save as preset',
      saveDisabledTooltip: 'Add at least one keyword or CV Search requirement to save a preset.',
    },
    browser: {
      actionError: 'The last action failed. Try again.',
      authorAll: 'All authors',
      authorLabel: 'Author',
      close: 'Close',
      delete: 'Delete',
      deleteConfirm: 'Delete saved filter "{{name}}"? This cannot be undone.',
      empty: 'No saved filters found.',
      error: 'Could not load saved filters.',
      helpLabel: 'What do these buttons do?',
      helpLoad: 'Load copies the preset criteria into the search form.',
      helpOverwrite:
        'Overwrite replaces the preset content with the criteria currently shown (own presets only).',
      load: 'Load',
      searchPlaceholder: 'Search by name',
      summaryMustHave_one: '1 must-have CV Search requirement',
      summaryMustHave_other: '{{count}} must-have CV Search requirements',
      summaryNiceToHave_one: '1 nice-to-have CV Search requirement',
      summaryNiceToHave_other: '{{count}} nice-to-have CV Search requirements',
      summaryTags_one: '1 keyword',
      summaryTags_other: '{{count}} keywords',
      title: 'Saved filters',
      unknownAuthor: 'Unknown author',
      update: 'Overwrite',
      updated: 'Updated {{date}}',
      updateDisabledTooltip:
        'Add at least one keyword or CV Search requirement before overwriting a preset.',
    },
    saveDialog: {
      cancel: 'Cancel',
      conflict: 'A preset with this name already exists.',
      error: 'Could not save the preset. Try again.',
      nameLabel: 'Preset name',
      namePlaceholder: 'e.g. Backend developers',
      overwrite: 'Overwrite',
      save: 'Save',
      title: 'Save criteria as preset',
    },
  },
  startButton: 'Start CV Search',
  startDialog: {
    cancel: 'Cancel',
    confirm: 'Start',
    emptyNameError: 'Enter a CV Search name.',
    nameLabel: 'Search name',
    title: 'Start CV Search',
  },
  startDisabledTooltip:
    'CV Search can be started only for a process that is active, closed, awaiting configuration, or awaiting restart after an error.',
};
